import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtMoney } from "@/lib/api";
import { useMasters, opt, partiesByRole } from "@/lib/useMasters";
import { Card, Field, SelectInput, TextInput, NumberInput, StatusBadge, CrudActions } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";

export default function LabourAgreements() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["labour-agreements"],
    queryFn: async () => (await api.get("/masters/labour-agreements")).data,
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm(`Close agreement for ${row.contractor_name}?`)) return;
    try {
      await api.delete(`/masters/labour-agreements/${row.id}`);
      toast.success("Agreement closed");
      qc.invalidateQueries({ queryKey: ["labour-agreements"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid="add-labour-agreement-btn" onClick={() => { setEditing(null); setOpen(true); }}
          className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Plus size={16} /> New Agreement
        </button>
      </div>
      {isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                <th className="px-4 py-3">Contractor</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Rates</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-400">No agreements</td></tr>}
              {data.map((a) => (
                <tr key={a.id} className="border-t border-stone-100 even:bg-stone-50/50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.contractor_name}</div>
                    <div className="text-xs text-stone-400 font-mono">{a.contractor_code}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{a.effective_from || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {(a.rates || []).map((r) => (
                        <div key={r.id} className="text-xs text-stone-600">
                          <span className="font-medium text-stone-800">{r.bag_type_name}</span>
                          {" · Load "}{fmtMoney(r.loading_rate)}
                          {" · Unload "}{fmtMoney(r.unloading_rate)}
                          {" · Replace/Fill "}{fmtMoney(r.replace_fill_rate)}
                        </div>
                      ))}
                      {(!a.rates || !a.rates.length) && <span className="text-xs text-stone-400">No bag rates</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge tone={a.active === false || a.status === "Closed" ? "neutral" : "emerald"}>{a.status}</StatusBadge></td>
                  <td className="px-4 py-3">
                    <CrudActions testid={`labour-ag-${a.id}`} onEdit={() => { setEditing(a); setOpen(true); }} onDelete={() => remove(a)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <AgreementDialog open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={() => qc.invalidateQueries({ queryKey: ["labour-agreements"] })} />
    </div>
  );
}

function AgreementDialog({ open, onClose, record, onSaved }) {
  const m = useMasters();
  const contractors = opt(partiesByRole(m.parties, "Labour Contractor"), (p) => `${p.name} (${p.code})`);
  const [f, setF] = useState({ contractor_id: "", effective_from: new Date().toISOString().slice(0, 10), remarks: "" });
  const [rates, setRates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      setF({
        contractor_id: record.contractor_id || "",
        effective_from: (record.effective_from || "").slice(0, 10),
        remarks: record.remarks || "",
      });
      setRates((record.rates || []).map((r) => ({
        bag_type_id: r.bag_type_id,
        loading_rate: r.loading_rate,
        unloading_rate: r.unloading_rate,
        replace_fill_rate: r.replace_fill_rate,
      })));
    } else {
      setF({ contractor_id: "", effective_from: new Date().toISOString().slice(0, 10), remarks: "" });
      setRates((m.bagTypes || []).filter((b) => b.active !== false).map((b) => ({
        bag_type_id: b.id, loading_rate: "", unloading_rate: "", replace_fill_rate: "",
      })));
    }
  }, [open, record, m.bagTypes]);

  const setRate = (i, k, v) => setRates((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    if (!f.contractor_id) return toast.error("Select a labour contractor");
    setSaving(true);
    try {
      const body = {
        ...f,
        contractor_id: +f.contractor_id,
        rates: rates.filter((r) => r.bag_type_id).map((r) => ({
          bag_type_id: +r.bag_type_id,
          loading_rate: +r.loading_rate || 0,
          unloading_rate: +r.unloading_rate || 0,
          replace_fill_rate: +r.replace_fill_rate || 0,
        })),
      };
      if (record) await api.put(`/masters/labour-agreements/${record.id}`, body);
      else await api.post("/masters/labour-agreements", body);
      toast.success(record ? "Agreement updated" : "Agreement saved");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const bagName = (id) => (m.bagTypes || []).find((b) => String(b.id) === String(id))?.name || `Bag #${id}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="labour-agreement-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Labour Agreement" : "New Labour Agreement"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Labour Contractor" required>
            <SelectInput options={contractors} value={f.contractor_id} onChange={(e) => setF({ ...f, contractor_id: e.target.value })} data-testid="la-contractor" />
          </Field>
          <Field label="Effective From">
            <TextInput type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} />
          </Field>
          <Field label="Remarks" className="col-span-2">
            <TextInput value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
          </Field>
        </div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mt-1">Rates by bag type (₹ / bag)</div>
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
              <tr>
                <th className="px-3 py-2">Bag Type</th>
                <th className="px-3 py-2 text-right">Loading</th>
                <th className="px-3 py-2 text-right">Unloading</th>
                <th className="px-3 py-2 text-right">Replace / Fill</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={r.bag_type_id || i} className="border-t border-stone-100">
                  <td className="px-3 py-2 font-medium">{bagName(r.bag_type_id)}</td>
                  <td className="px-3 py-2"><NumberInput value={r.loading_rate} onChange={(e) => setRate(i, "loading_rate", e.target.value)} /></td>
                  <td className="px-3 py-2"><NumberInput value={r.unloading_rate} onChange={(e) => setRate(i, "unloading_rate", e.target.value)} /></td>
                  <td className="px-3 py-2"><NumberInput value={r.replace_fill_rate} onChange={(e) => setRate(i, "replace_fill_rate", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-400">Replace / Fill is used for torn bag replacement and spillage filling labour on inward and outward.</p>
        <button onClick={save} disabled={saving} data-testid="save-labour-agreement-btn"
          className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {record ? "Update Agreement" : "Save Agreement"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
